import React, { useMemo } from 'react';
import UiCard from '@/components/ui/Card';
import { ImageModelResponseType, QuestionOrQuestionGroupType } from '@/components/admin/QuestionImage/utils';
import Edit from '@/components/admin/QuestionImage/Edit';
import Delete from '@/components/admin/QuestionImage/Delete';
import DisplayLocationDropdown from '@/components/admin/QuestionImage/DisplayLocationDropdown';
import useQuestionLocaleToggleListener from '@/hooks/useQuestionLocaleToggleListener';
import cn from '@/utils/cn';
import Tooltip from '@/components/common/Tooltip';

interface CardProps {
  afterSubmit: (imageModel?: ImageModelResponseType[] | any) => any;
  afterDelete: (imageModelId?: number) => void;
  forAnswerKey?: boolean;
  imageModel: ImageModelResponseType;
  mediaType: string;
  modelId: number;
  modelType: 'Question' | 'QuestionGroup';
  questionType: QuestionOrQuestionGroupType;
  userType: UserType;
}

const Card = ({
  afterSubmit,
  afterDelete,
  forAnswerKey,
  imageModel,
  modelId,
  modelType,
  questionType,
  userType,
}: CardProps) => {
  const { image } = imageModel;
  const hasAlignmentDropdown = questionType === 'DrawingQuestion';
  const inTable = imageModel?.media_meta?.type === 'in_grid';
  const [dimX, dimY] = imageModel.dimensions.split('x');
  const { isInSpanish } = useQuestionLocaleToggleListener({ modelId, forAnswerKey, modelType });

  const url = useMemo(() => {
    if (isInSpanish && image?.es_url) return image.es_url;

    return image.url;
  }, [image?.url, image?.es_url, isInSpanish]);

  const isGif = image.image_en_file_name.toLowerCase().endsWith('.gif');
  const displayLocation = inTable ? 'Table' : 'Prompt';

  const selectStyles = {
    control: base => ({
      ...base,
      minHeight: '1.5rem',
      minWidth: '8.5rem',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      display: state.isDisabled ? 'none' : '',
      padding: 0,
    }),
    singleValue: base => ({
      ...base,
      paddingBlock: '0.25rem',
    }),
    valueContainer: base => ({
      ...base,
      padding: '0 0.25rem',
    }),
  };

  const renderIcon = () => (
    isGif ? (
      <Tooltip content="GIFs will not show up in print and can only be accessed online">
        <i aria-hidden="true" className="fa fa-exclamation-circle tw-text-yellow-700" />
      </Tooltip>
    ) : (
      <i aria-hidden="true" className="fa fa-file-image-o" />
    )
  );

  const renderImage = () => (
    <a
      className={cn(
        'tw-max-w-16 tw-min-w-16 tw-max-h-16 tw-min-h-16 tw-flex tw-items-center tw-justify-center',
        'tw-border-0 tw-border-r-2 tw-border-solid tw-border-gray-300 tw-py-1 tw-px-2',
      )}
      href={url}
      rel="noopener noreferrer"
      target="_blank"
    >
      <img
        alt="thumbnail"
        className="tw-max-w-16 tw-max-h-16 tw-border tw-border-gray-200"
        loading="lazy"
        src={url}
        style={{ cursor: 'pointer', maxHeight: '64', maxWidth: '64' }}
      />
    </a>
  );

  const renderInfo = () => (
    <div className="tw-flex-grow tw-flex tw-flex-col tw-gap-1">
      <div className="tw-flex tw-items-center tw-gap-2">
        {hasAlignmentDropdown ? (
          <DisplayLocationDropdown
            inImageCard
            afterSubmit={afterSubmit}
            imageModel={imageModel}
            selectStyles={selectStyles}
          />
        ) : (
          <span className="tw-text-gray-700">{displayLocation}</span>
        )}
      </div>
      <div className="tw-text-sm tw-text-gray-500">
        {`${dimX} ✕ ${dimY} px `}
      </div>
    </div>
  );

  const renderActions = () => (
    <div className="tw-flex tw-flex-row tw-gap-1 tw-ml-1 tw-items-end">
      <Edit
        useIconOnly
        afterSubmit={afterSubmit}
        imageModel={imageModel}
        mediaType={imageModel.media_meta.type}
        modelId={modelId}
        modelType={modelType}
        userType={userType}
      />
      <Delete
        useIconOnly
        afterDelete={afterDelete}
        imageModel={imageModel}
      />
    </div>
  );

  return (
    <UiCard
      className={cn(
        'tw-flex tw-justify-between tw-gap-2 tw-p-2 tw-shadow-lg tw-border-solid tw-border-2 tw-border-gray-300',
        'tw-min-h-0 tw-h-fit tw-min-w-[366px] tw-max-w-[366px] tw-rounded-lg tw-overflow-visible',
      )}
    >
      {renderIcon()}
      {renderImage()}
      <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-w-full">
        {renderInfo()}
        {renderActions()}
      </div>
    </UiCard>
  );
};

export default Card;
